import { ViewConfig } from "datasheets/model/core/Workbook";
import {
  currentViewConfigAtom,
  configForViewAtom,
  viewForKeyAtom,
} from "datasheets/model/atoms/WorkbookFocusAtoms";
import { WritableAtom, atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { FormViewConfig } from "../FormViewConfig";
import { useMemo } from "react";
import { uuid } from "datasheets/model/utils/uuid";

export const componentConfigForFormViewAtom = (
  formView: WritableAtom<
    ViewConfig,
    ViewConfig | ((prev: ViewConfig) => ViewConfig)
  >,
  componentId: string
) =>
  atom(
    (get) => {
      const view = get(formView) as FormViewConfig;
      const component = view.components[componentId];
      return component;
    },
    (get, set, arg) => {
      const prevView = get(formView) as FormViewConfig;
      const prevComponent = prevView.components[componentId];
      const newComponent = typeof arg === "function" ? arg(prevComponent) : arg;
      set(formView, {
        ...prevView,
        ...{
          components: {
            ...prevView.components,
            ...{ [newComponent.id]: newComponent },
          },
        },
      });
    }
  );

export const useComponentConfigForViewKeyAtom = (
  viewId: string,
  componentId: string
) => {
  const viewAtom = useMemo(() => viewForKeyAtom(viewId), [viewId]);
  const formViewAtom = useMemo(() => configForViewAtom(viewAtom), [viewAtom]);
  return useAtom(
    useMemo(
      () => componentConfigForFormViewAtom(formViewAtom, componentId),
      [formViewAtom, componentId]
    )
  );
};

export const useComponentConfigAtom = (componentId: string) => {
  return useAtom(
    useMemo(
      () => componentConfigForFormViewAtom(currentViewConfigAtom, componentId),
      [componentId]
    )
  );
};

export const componentCustomConfigAtom = (componentId: string) => {
  const configAtom = componentConfigForFormViewAtom(
    currentViewConfigAtom,
    componentId
  );
  return atom(
    (get) => {
      const formConfig = get(configAtom);
      return formConfig.config;
    },
    (get, set, arg) => {
      const formConfig = get(configAtom);
      const prevConfig = formConfig.config;
      const newConfig = typeof arg === "function" ? arg(prevConfig) : arg;
      set(configAtom, { ...formConfig, ...{ config: newConfig } });
    }
  );
};

export const useComponentCustomConfigAtom = (componentId: string) => {
  return useAtom(
    useMemo(() => componentCustomConfigAtom(componentId), [componentId])
  );
};

export const componentEditConfigAtom = (componentId: string) => {
  const configAtom = componentConfigForFormViewAtom(
    currentViewConfigAtom,
    componentId
  );
  return atom(
    (get) => {
      const formConfig = get(configAtom);
      return formConfig.editConfig;
    },
    (get, set, arg) => {
      const formConfig = get(configAtom);
      const prevConfig = formConfig.editConfig;
      const newConfig = typeof arg === "function" ? arg(prevConfig) : arg;
      set(configAtom, { ...formConfig, ...{ editConfig: newConfig } });
    }
  );
};

export const useComponentEditConfigAtom = (componentId: string) => {
  return useAtom(
    useMemo(() => componentEditConfigAtom(componentId), [componentId])
  );
};

export const componentCustomOrEditConfigAtom = (componentId: string) => {
  const configAtom = componentConfigForFormViewAtom(
    currentViewConfigAtom,
    componentId
  );
  return atom(
    (get) => {
      const view = get(currentViewConfigAtom);
      const formConfig = get(configAtom);
      return view.edit ? formConfig.editConfig : formConfig.config;
    },
    (get, set, arg) => {
      const view = get(currentViewConfigAtom);
      const formConfig = get(configAtom);
      const prevConfig = view.edit ? formConfig.editConfig : formConfig.config;
      const newConfig = typeof arg === "function" ? arg(prevConfig) : arg;
      set(configAtom, {
        ...formConfig,
        ...(view.edit ? { editConfig: newConfig } : { config: newConfig }),
      });
    }
  );
};

export const useComponentCustomOrEditConfigAtom = (componentId: string) => {
  return useAtom(
    useMemo(() => componentCustomOrEditConfigAtom(componentId), [componentId])
  );
};

//////////////////////////
// Wires
type WireStoreType = { [id: string]: any };
export const wireStoreAtom = atom<WireStoreType>({});

export const wireAtomFor = (wireId: string | undefined) => {
  return atom(
    (get) => {
      if (!wireId) {
        return undefined;
      }
      const wireStore = get(wireStoreAtom);
      return wireStore[wireId];
    },
    (get, set, arg) => {
      if (!wireId) {
        return;
      }
      const wireStore = get(wireStoreAtom);
      const prevConfig = wireStore[wireId];
      const newConfig = typeof arg === "function" ? arg(prevConfig) : arg;
      set(wireStoreAtom, { ...wireStore, ...{ [wireId]: newConfig } });
    }
  );
};

export const newWire = () => {
  return uuid();
};

export const useInputWire = (wireId: string | undefined) => {
  return useAtomValue(useMemo(() => wireAtomFor(wireId), [wireId]));
};

export const useOutputWire = (wireId: string | undefined) => {
  return useSetAtom(useMemo(() => wireAtomFor(wireId), [wireId]));
};
